import { type ReactNode, type Ref, use } from 'react'
import { Accordion as BaseAccordion } from '@base-ui/react/accordion'

import { cn } from '@gto/ui/cn'
import { IconButton } from '@gto/ui/icon-button'
import { ChevronDownIcon, LightBulbIcon } from '@gto/ui/icons/outline'

import { type AccordionSize, type AccordionVariant, accordionVariants } from './accordion.styles'
import { AccordionSizeContext, AccordionVariantContext } from './accordion-context'

export type TriggerProps = {
  /** Override the variant inherited from `Accordion.Root`. */
  variant?: AccordionVariant
  /** Override the size inherited from `Accordion.Root`. */
  size?: AccordionSize
  children?: ReactNode
  className?: string
  /**
   * Leading icon. `undefined` → variant default (lightbulb for `inline`, none
   * for `card`). Pass `null` to suppress; pass any node to override.
   */
  leadingIcon?: ReactNode
  /**
   * Trailing indicator icon. `undefined` → variant default (chevron for
   * `inline`, plus for `card`). Pass `null` to suppress; pass any node to
   * override.
   */
  trailingIcon?: ReactNode
  ref?: Ref<HTMLButtonElement>
}

const renderDefaultLeading = (variant: AccordionVariant) => {
  if (variant === 'card') {
    return null
  }

  return (
    <IconButton
      as="span"
      color="primary"
      size="small"
      variant="text"
    >
      <LightBulbIcon />
    </IconButton>
  )
}

const renderDefaultTrailing = (variant: AccordionVariant, indicatorClassName: string) => {
  return (
    <IconButton
      as="span"
      className={cn(variant === 'card' && 'ml-auto')}
      color="secondary"
      size="small"
      variant="text"
    >
      <ChevronDownIcon className={indicatorClassName} />
    </IconButton>
  )
}

export const Trigger = ({
  variant: variantProp,
  size: sizeProp,
  children,
  className,
  leadingIcon,
  trailingIcon,
  ref,
}: TriggerProps) => {
  const variant = variantProp ?? use(AccordionVariantContext)
  const size = sizeProp ?? use(AccordionSizeContext)
  const { header, trigger, indicator } = accordionVariants({ variant, size })

  const resolvedLeading = leadingIcon === undefined ? renderDefaultLeading(variant) : leadingIcon
  const resolvedTrailing =
    trailingIcon === undefined ? renderDefaultTrailing(variant, indicator()) : trailingIcon

  return (
    <BaseAccordion.Header className={header()}>
      <BaseAccordion.Trigger
        ref={ref}
        className={cn(trigger(), className)}
      >
        {resolvedLeading}
        {children}
        {resolvedTrailing}
      </BaseAccordion.Trigger>
    </BaseAccordion.Header>
  )
}
